// Keyboard-wedge capture — chapter 7 §7.2 (R7.3). A hardware scanner "types" the
// code quickly and ends with a terminator (Enter by default). We accumulate keystrokes
// that arrive in rapid succession and emit the buffer on the terminator. ASCII 29 (GS,
// FNC1 in GS1-128) arrives either as the literal control character or as a scanner-
// configured substitute; both are normalised to GS here.

import { GS } from "@trail/core";
import { getPrefs } from "../api/client.js";
import { onNativeScan } from "./native.js";

export interface WedgeOptions {
  /** Max gap between keystrokes to be considered one scan (ms). */
  maxGapMs?: number;
  /** Characters a scanner may be configured to send instead of GS (ASCII 29). */
  gsSubstitutes?: string[];
  onScan: (data: string) => void;
}

/** Something the user is typing into that is not our scan field. */
function otherField(el: EventTarget | null, target: HTMLInputElement): boolean {
  if (!(el instanceof HTMLElement) || el === target) return false;
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || el.isContentEditable;
}

/** Listen for scanner keystrokes on the whole document while a scan screen is up (owner
 *  2026-10-28: a handheld scanner must work without the field being focused, and the
 *  on-screen keyboard must not be needed). Keystrokes into another field are left alone. */
export function attachWedge(target: HTMLInputElement, opts: WedgeOptions): () => void {
  // Bluetooth HID scanners on a phone deliver keystrokes with pauses well above 50 ms (the
  // owner's Socket Mobile S730, 2026-10-28: the SSCC arrived cut and was "not found"); a
  // person types slower than four keys a second, so 300 ms still tells the two apart.
  const maxGap = opts.maxGapMs ?? 300;
  // The device setting names the substitutes its scanner sends ("~" and "|" are common); GS itself always counts.
  const subs = opts.gsSubstitutes ?? ["\u001d", ...getPrefs().gsSubstitutes.split("")];
  let buffer = "";
  let last = 0;

  const deliver = (data: string) => {
    let normalised = data;
    for (const s of subs) if (s !== GS) normalised = normalised.split(s).join(GS);
    opts.onScan(normalised);
  };
  const flush = () => {
    const data = buffer.length >= target.value.length ? buffer : target.value;
    buffer = "";
    target.value = "";
    if (data.length === 0) return;
    deliver(data);
  };

  const onKey = (ev: KeyboardEvent) => {
    if (otherField(ev.target, target) || ev.metaKey || ev.ctrlKey || ev.altKey) return;
    const inField = ev.target === target;
    const now = performance.now();
    if (now - last > maxGap && buffer.length > 0 && ev.key !== "Enter" && !inField) {
      // Human-speed typing after a pause outside the field: the earlier buffer is abandoned.
      buffer = "";
    }
    last = now;
    if (ev.key === "Enter" || ev.key === "Tab") {
      ev.preventDefault();
      flush();
      return;
    }
    if (ev.key.length === 1) {
      buffer += ev.key;
      // Outside the field the keystroke must not scroll or type anywhere; in the field it may show.
      if (!inField) ev.preventDefault();
    }
  };
  const onInput = () => {
    // IME/composition keyboards deliver no usable keydown: the field's value is the buffer.
    if (target.value.length >= buffer.length) buffer = target.value;
  };

  // A person who typed into the field and closed the keyboard with iOS's "Klar" sent no Enter
  // (owner 2026-11-03: the article number typed "did not work"): what stands in the field goes.
  const onBlur = () => { if (target.value.length > 0) flush(); };
  document.addEventListener("keydown", onKey, true);
  target.addEventListener("input", onInput);
  target.addEventListener("blur", onBlur);
  // Inside the native shell a dedicated scanner's reads arrive as events, not keystrokes.
  const offNative = onNativeScan(deliver);
  return () => {
    target.removeEventListener("blur", onBlur);
    document.removeEventListener("keydown", onKey, true);
    target.removeEventListener("input", onInput);
    offNative();
  };
}
